import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { T } from "@/context/TextEditContext";

/**
 * ReviewsSummaryStrip — compact "★ 4.8 · 24 reviews" pill on the homepage
 * that links to the full reviews page. Hides itself gracefully if the
 * fetch fails or there are zero reviews yet (nudges to "Be the first").
 */
export default function ReviewsSummaryStrip() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/reviews", { params: { limit: 1 } });
        if (!cancelled) setData(res.data);
      } catch {
        /* silent — strip just doesn't render */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data) return null;

  const hasAny = data.count > 0;

  return (
    <Link
      to="/reviews"
      data-testid="reviews-summary-strip"
      className="mx-4 mt-3 bg-white border border-stone-200 rounded-xl px-4 py-3 flex items-center gap-3 hover:border-brand-red transition-colors"
    >
      <div className="w-9 h-9 rounded-full bg-brand-yellow/20 flex items-center justify-center shrink-0">
        <Star className="w-5 h-5 text-brand-yellow fill-brand-yellow" />
      </div>
      <div className="flex-1 min-w-0">
        {hasAny ? (
          <>
            <div className="text-sm font-black text-stone-800 leading-tight">
              {data.average.toFixed(1)} · {data.count} review
              {data.count === 1 ? "" : "s"}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5">
              <T k="reviews.strip_sub" d="Tap to read what people are saying · leave one too" />
            </div>
          </>
        ) : (
          <>
            <div className="text-sm font-black text-stone-800 leading-tight">
              <T k="reviews.strip_first" d="Be the first to review us" />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5">
              <T k="reviews.strip_first_sub" d="Tap to leave a 5-star ★ rating" />
            </div>
          </>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-stone-400" />
    </Link>
  );
}
